const BASE = (process.env.REACT_APP_API_URL || "") + "/api";

// All requests include credentials so the HttpOnly auth cookie is sent automatically.
// Never read or write the token from JavaScript — it lives only in the cookie.
async function request(url, options = {}) {
  const res = await fetch(BASE + url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

export const api = {
  // ── Auth ──────────────────────────────────────────────────
  login: (company_code, username, password) =>
    request("/auth/login", { method: "POST", body: JSON.stringify({ company_code, username, password }) }),

  logout: () =>
    request("/auth/logout", { method: "POST" }),

  changePassword: (current_password, new_password) =>
    request("/auth/change-password", { method: "PUT", body: JSON.stringify({ current_password, new_password }) }),

  // ── Companies (super_user) ────────────────────────────────
  listCompanies: () => request("/companies"),

  createCompany: (data) =>
    request("/companies", { method: "POST", body: JSON.stringify(data) }),

  updateCompany: (id, data) =>
    request(`/companies/${id}`, { method: "PUT", body: JSON.stringify(data) }),

  deleteCompany: (id) =>
    request(`/companies/${id}`, { method: "DELETE" }),

  // ── Company Profile (admin) ───────────────────────────────
  getCompanyProfile: () => request("/company/profile"),

  updateCompanyProfile: (data) =>
    request("/company/profile", { method: "PUT", body: JSON.stringify(data) }),

  // ── Company Letterhead ────────────────────────────────────
  getCompanyLetterhead: () => request("/company/letterhead"),

  updateCompanyLetterhead: (letterhead_png) =>
    request("/company/letterhead", { method: "PUT", body: JSON.stringify({ letterhead_png }) }),

  // ── Company Custom Banks ──────────────────────────────────
  getCompanyBanks: () => request("/company/banks"),

  updateCompanyBanks: (banks) =>
    request("/company/banks", { method: "PUT", body: JSON.stringify({ banks }) }),

  // ── Company Valuators ─────────────────────────────────────
  getCompanyValuators: () => request("/company/valuators"),

  updateCompanyValuators: (valuators) =>
    request("/company/valuators", { method: "PUT", body: JSON.stringify({ valuators }) }),

  // ── Users ─────────────────────────────────────────────────
  listUsers: () => request("/users"),

  createUser: (data) =>
    request("/users", { method: "POST", body: JSON.stringify(data) }),

  updateUser: (id, data) =>
    request(`/users/${id}`, { method: "PUT", body: JSON.stringify(data) }),

  deleteUser: (id) =>
    request(`/users/${id}`, { method: "DELETE" }),

  // ── Reports ───────────────────────────────────────────────
  listReports: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/reports${qs ? "?" + qs : ""}`);
  },

  getReport: (id) => request(`/reports/${id}`),

  saveReport: (state, filename) =>
    request("/reports", { method: "POST", body: JSON.stringify({ state, filename }) }),

  updateReport: (id, state, filename) =>
    request(`/reports/${id}`, { method: "PUT", body: JSON.stringify({ state, filename }) }),

  deleteReport: (id) =>
    request(`/reports/${id}`, { method: "DELETE" }),

  // ── Print tracking ────────────────────────────────────────
  recordPrint: (id, print_type, action = "print") =>
    request(`/reports/${id}/print`, { method: "POST", body: JSON.stringify({ print_type, action }) }),

  // ── Admin report list ─────────────────────────────────────
  listAdminReports: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/admin/reports${qs ? "?" + qs : ""}`);
  },

  // ── Stats ─────────────────────────────────────────────────
  getReportStats: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/stats/reports${qs ? "?" + qs : ""}`);
  },

  // ── Map data ──────────────────────────────────────────────
  getMapData: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/map/data${qs ? "?" + qs : ""}`);
  },
};
